import { useEffect, useState, useRef, Dispatch, SetStateAction } from 'react';

interface UseLocalStorageStateOptions<T> {
  serialize?: (value: T) => string;
  deserialize?: (value: string) => T;
}

type DefaultValue<T> = T | (() => T);

function useLocalStorageState<T>(
  key: string,
  defaultValue?: DefaultValue<T>,
  options?: UseLocalStorageStateOptions<T>
): [T, Dispatch<SetStateAction<T>>] {
  const { serialize = JSON.stringify, deserialize = JSON.parse } = options || {};

  const getStorageValue = (): T => {
    const valueInLocalStorage = window.localStorage.getItem(key);
    if (valueInLocalStorage) {
      try {
        return deserialize(valueInLocalStorage) as T;
      } catch {
        // Failed to parse - remove invalid entry and return default
        window.localStorage.removeItem(key);
      }
    }
    return typeof defaultValue === 'function' ? (defaultValue as () => T)() : (defaultValue as T);
  };

  const [isInitialized, setIsInitialized] = useState(false);
  const [state, setState] = useState<T>(defaultValue as T);

  const prevKeyRef = useRef<string>(key);

  useEffect(() => {
    if (!isInitialized) return;
    const prevKey = prevKeyRef.current;
    if (prevKey !== key) {
      window.localStorage.removeItem(prevKey);
    }
    prevKeyRef.current = key;
    window.localStorage.setItem(key, serialize(state));
  }, [key, state, serialize, isInitialized]);

  useEffect(() => {
    setState(getStorageValue());
    setIsInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [state, setState];
}

export default useLocalStorageState;
